// src/firebase/useAuth.js
import { useState, useEffect, createContext, useContext } from "react";
import {
  onAuthStateChanged,
  signInAnonymously,
  signInWithPopup,
  linkWithPopup,
  linkWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  signOut,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebaseConfig";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        setIsAnonymous(firebaseUser.isAnonymous);
      } else {
        await signInAnonymously(auth);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const saveData = async (toolId, data) => {
    if (!user) return;
    await setDoc(
      doc(db, "users", user.uid, "tools", toolId),
      { ...data, updatedAt: serverTimestamp() },
      { merge: true }
    );
  };

  const loadData = async (toolId) => {
    if (!user) return null;
    const snap = await getDoc(doc(db, "users", user.uid, "tools", toolId));
    return snap.exists() ? snap.data() : null;
  };

  const upgradeWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await linkWithPopup(auth.currentUser, provider);
      setIsAnonymous(false);
    } catch (error) {
      if (error.code === "auth/credential-already-in-use") {
        const anonymousUID = auth.currentUser.uid;
        await signInWithPopup(auth, provider);
        await mergeAnonymousData(anonymousUID, auth.currentUser.uid);
        setIsAnonymous(false);
      } else {
        throw error;
      }
    }
  };

  const upgradeWithEmail = async (email, password) => {
    const credential = EmailAuthProvider.credential(email, password);
    try {
      await linkWithCredential(auth.currentUser, credential);
      setIsAnonymous(false);
    } catch (error) {
      if (error.code === "auth/credential-already-in-use") {
        const anonymousUID = auth.currentUser.uid;
        const { signInWithEmailAndPassword } = await import("firebase/auth");
        await signInWithEmailAndPassword(auth, email, password);
        await mergeAnonymousData(anonymousUID, auth.currentUser.uid);
        setIsAnonymous(false);
      } else {
        throw error;
      }
    }
  };

  const mergeAnonymousData = async (anonymousUID, realUID) => {
    console.log(`Merging data from ${anonymousUID} to ${realUID}`);
  };

  const logout = () => signOut(auth);

  return (
    <AuthContext.Provider
      value={{ user, isAnonymous, loading, saveData, loadData, upgradeWithGoogle, upgradeWithEmail, logout }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
